import { useState, useRef, useLayoutEffect } from 'react'
import { debounce } from 'lodash-es'

type Size = {
  width: number
  height: number
}

export function useParentSize<T extends Element>(ref: React.RefObject<T>, ms?: number): [Size]
export function useParentSize<T extends Element>(ms?: number): [React.RefObject<T>, Size]
export function useParentSize<T extends Element>(a: React.RefObject<T> | number, b?: number) {
  const givenRef = typeof a !== 'number' ? a : undefined
  const debounceMs = typeof a === 'number' ? a : b ?? 300

  const [size, setSize] = useState({ width: 0, height: 0 })
  const ref = givenRef ?? useRef<T>()

  const saveParentSize = (node: T) => setSize(computeSize(node.parentNode as Element))

  useLayoutEffect(() => {
    const handleWindowResize = debounce(() => {
      const node = ref.current
      if (!node) return
      saveParentSize(node)
    }, debounceMs)

    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [debounceMs, ref])

  return givenRef ? [size] : [ref, size]
}

function computeSize<T extends Element>(node: T): Size {
  const cs = window.getComputedStyle(node)

  const paddingLeft = parseFloat(cs.paddingLeft)
  const paddingRight = parseFloat(cs.paddingRight)
  const paddingTop = parseFloat(cs.paddingTop)
  const paddingBottom = parseFloat(cs.paddingBottom)

  const width = parseFloat(cs.getPropertyValue('width')) - paddingLeft - paddingRight
  const height = parseFloat(cs.getPropertyValue('height')) - paddingTop - paddingBottom

  return { width, height }
}
